import React, { useState, useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from 'react-i18next';

import {
    CapsuleButton,
    ContentSpace,
    ListedGroup,
    MainPageWithButtons,
} from "../_components";
import { Selectors } from "../_reducers/app.reducer";
import { SimpleListElement } from "../../Common/_components/SimpleListElement";
import { Icons } from "../_assets";
import { popUpActions } from "../_stores/PopUpStore/popUpActions";
import { genericActions } from "../_actions/generic.actions";
import { ConnectedAppsService } from "../_services/ConnectedAppsService";
import { TimeHelper } from "../_helpers";
import { GLOBAL_POPUPS, LOCATION_PERMISSIONS } from "../_constants";

// Same wording as the OAuth consent screen: a permission applies when the app
// holds both its access scope and its resource scope.
const PERMISSIONS = [
    { access: "read",  resource: "devices",     key: "oauth_permission_read_devices" },
    { access: "write", resource: "devices",     key: "oauth_permission_write_devices" },
    { access: "read",  resource: "locations",   key: "oauth_permission_read_locations" },
    { access: "read",  resource: "invitations", key: "oauth_permission_read_invitations" },
    { access: "write", resource: "invitations", key: "oauth_permission_write_invitations" },
];

export function ConnectedAppsModule() {

    //LIBRARIES
    const dispatch = useDispatch();
    const { t } = useTranslation();

    //GLOBAL STATE
    const locations = useSelector(Selectors.getLocations);

    //LOCAL STATE
    const [apps, setApps] = useState(null);
    const [editing, setEditing] = useState(null);

    // The backend only accepts the locations the user owns
    const ownedLocations = (locations || [])
        .filter(location => location.userType === LOCATION_PERMISSIONS.OWNER);

    //ACTION HANDLERS
    const handleRevoke = useCallback(app => {
        dispatch(popUpActions.showPopUp(GLOBAL_POPUPS.BASE_APP_GLOBAL, {
            title: t("connected_apps_revoke_title"),
            body: t("connected_apps_revoke_body", { name: app.name }),
            onAccept: () => dispatch(genericActions.genericAsyncAction(async () => {
                await ConnectedAppsService.revoke(app.clientId);
                setApps(current => current.filter(({ clientId }) => clientId !== app.clientId));
            })),
            acceptButtonName: t("connected_apps_revoke"),
            cancelButtonName: t("global_cancel"),
            limitWidth: true,
        }));
    }, [dispatch, t]);

    const handleEdit = useCallback(app => {
        // Drop the locations the user no longer owns: the backend would reject
        // them and the editor has no checkbox to untick them
        const ownedUids = ownedLocations.map(({ uid }) => uid);
        setEditing({
            clientId: app.clientId,
            allLocations: app.allLocations,
            locationUids: app.locations.map(({ uid }) => uid).filter(uid => ownedUids.includes(uid)),
        });
    }, [ownedLocations]);

    const handleSave = useCallback(() => {
        const { clientId, allLocations, locationUids } = editing;
        dispatch(genericActions.genericAsyncAction(async () => {
            const updated = await ConnectedAppsService.update(clientId, {
                allLocations,
                locationUids: allLocations ? [] : locationUids,
            });
            setApps(current => current.map(app => app.clientId === clientId ? updated : app));
            setEditing(null);
        }));
    }, [dispatch, editing]);

    //INTIALIZATION
    useEffect(() => {
        dispatch(genericActions.genericAsyncAction(async () => {
            setApps(await ConnectedAppsService.list());
        }));
    }, [dispatch]);

    //VIEW
    return (
        <ContentSpace
            mainContent={<MainPageWithButtons
                pageTitle={t("mod_connected_apps_title")}
                content={content()}
            />}
        />
    );

    function content() {
        return (
            <div>
                <div className="mt-2" />
                <div className="mb-2">{t("connected_apps_description")}</div>
                {apps && (
                    <ListedGroup
                        headerText={t("global_list")}
                        elements={getElements()}
                    />
                )}
            </div>
        );
    }

    function getElements() {
        if (!apps.length) {
            return [
                <SimpleListElement
                    key="empty"
                    text={
                        <div className="text-center">
                            {t("connected_apps_empty")}
                        </div>
                    }
                />
            ];
        }

        return apps.map(app =>
            <SimpleListElement
                key={app.clientId}
                text={appContent(app)}
            />
        );
    }

    function appContent(app) {
        const isEditing = editing?.clientId === app.clientId;
        return (
            <div className="py-1">
                <div className="d-flex justify-content-between align-items-start">
                    <div>
                        <div className="c-font-weight-medium">{app.name}</div>
                        {app.developer && (
                            <div className="text-secondary small">
                                {t("connected_apps_published_by")} {app.developer}
                            </div>
                        )}
                    </div>
                    <div className="d-flex">
                        {!isEditing && (
                            <CapsuleButton
                                testId="connected-app-edit"
                                icon={Icons.edit}
                                tooltip={t("global_edit")}
                                onClick={() => handleEdit(app)}
                                size={'SMALL'}
                                classNameExtra="mr-2"
                            />
                        )}
                        <CapsuleButton
                            testId="connected-app-revoke"
                            icon={Icons.trash}
                            tooltip={t("connected_apps_revoke")}
                            onClick={() => handleRevoke(app)}
                            size={'SMALL'}
                            style={`DANGER`}
                        />
                    </div>
                </div>

                <div className="mt-2">{t("connected_apps_can")}</div>
                <ul className="mb-2 pl-4">
                    {permissionsOf(app).map(key => <li key={key}>{t(key)}</li>)}
                </ul>

                <div>{t("connected_apps_locations")}</div>
                {isEditing ? locationsEditor() : locationsSummary(app)}

                <div className="mt-2 text-secondary small">
                    {t("connected_apps_authorized_on")}: <span>{formatDate(app.authorizedOn)}</span>
                    {" · "}
                    {t("connected_apps_last_used")}: <span>{
                        app.lastUsedOn
                            ? formatDate(app.lastUsedOn)
                            : t("connected_apps_never_used")
                    }</span>
                </div>
            </div>
        );
    }

    function permissionsOf(app) {
        return PERMISSIONS
            .filter(({ access, resource }) => app.scopes.includes(access) && app.scopes.includes(resource))
            .map(({ key }) => key);
    }

    function locationsSummary(app) {
        if (app.allLocations)
            return <div className="text-secondary">{t("connected_apps_all_locations")}</div>;

        return (
            <ul className="mb-0 pl-4">
                {app.locations.map(({ uid, name }) => <li key={uid}>{name}</li>)}
            </ul>
        );
    }

    function locationsEditor() {
        const { allLocations, locationUids } = editing;
        const canSave = allLocations || locationUids.length > 0;

        return (
            <div className="mt-1">
                <div className="form-check">
                    <input
                        className="form-check-input"
                        type="radio"
                        id="connected-app-all-locations"
                        checked={allLocations}
                        onChange={() => setEditing({ ...editing, allLocations: true })}
                    />
                    <label className="form-check-label" htmlFor="connected-app-all-locations">
                        {t("connected_apps_all_locations")}
                    </label>
                </div>
                <div className="form-check">
                    <input
                        className="form-check-input"
                        type="radio"
                        id="connected-app-only-these"
                        checked={!allLocations}
                        disabled={!ownedLocations.length}
                        onChange={() => setEditing({ ...editing, allLocations: false })}
                    />
                    <label className="form-check-label" htmlFor="connected-app-only-these">
                        {t("connected_apps_only_these")}
                    </label>
                </div>
                {!allLocations && (
                    <div className="ml-4">
                        {ownedLocations.map(({ uid, name }) => (
                            <div className="form-check" key={uid}>
                                <input
                                    className="form-check-input"
                                    type="checkbox"
                                    id={"connected-app-location-" + uid}
                                    checked={locationUids.includes(uid)}
                                    onChange={() => toggleLocation(uid)}
                                />
                                <label className="form-check-label" htmlFor={"connected-app-location-" + uid}>
                                    {name}
                                </label>
                            </div>
                        ))}
                    </div>
                )}
                <div className="d-flex justify-content-end mt-2">
                    <CapsuleButton
                        text={t("global_cancel")}
                        onClick={() => setEditing(null)}
                        size={'SMALL'}
                        classNameExtra="mr-2"
                    />
                    <CapsuleButton
                        testId="connected-app-save"
                        text={t("global_save")}
                        onClick={handleSave}
                        isEnabled={canSave}
                        size={'SMALL'}
                        fillStyle={'FILLED'}
                    />
                </div>
            </div>
        );
    }

    function toggleLocation(uid) {
        const { locationUids } = editing;
        setEditing({
            ...editing,
            locationUids: locationUids.includes(uid)
                ? locationUids.filter(other => other !== uid)
                : [...locationUids, uid],
        });
    }

    function formatDate(isoString) {
        return TimeHelper.isoStringToLocalTimezoneFormat(isoString, TimeHelper.getTimeFormats().DATE_NO_SECONDS);
    }
}
